import { useCallback, useState } from 'react'
import Info from '@/icons/info.svg?react'
import Graph from '@/icons/graph.svg?react'
import {
  Datagrid,
  DateField,
  FunctionField,
  Link,
  List,
  NumberField,
  SortPayload,
  TextField,
  useCreatePath
} from 'react-admin'
import { Box, IconButton, Tooltip } from '@mui/material'
import {
  useEarningsSummaryMonthly,
  DEFAULT_EARNINGS
} from '../hooks/useEarningsSummaryMonthly'
import TEarningsSummary, { EarningsSummaryDTO } from '../types/EarningsSummary'
import EarningsChart from './EarningsChart'
import EarningsSummary from './EarningsSummary'
import { currentUser, isVendor } from '@/authProvider'
import { useFetchEarnings } from '../hooks/useFetchEarnings'
import { EarningsFilter } from './EarningsFilter'
import { usePartner } from '@/hooks/usePartner'
import formatCurrency from '@/utils/formatCurrency'
import Modal from '@/components/Modal'
import { useEarningsModal } from '../hooks/useEarningsModal'
import { sendEarningsToEmail } from '../utils/sendEarningsToEmail'
import { EarningsCustomField } from './EarningsCustomField'
import { handleBreakdownCsv } from '../utils/handleBreakdownCsv'

const EarningsList = () => {
  const { currentStartDate, currentEndDate } = useEarningsSummaryMonthly()
  const [startDate, setStartDate] = useState<Date | null>(currentStartDate)
  const [endDate, setEndDate] = useState<Date | null>(currentEndDate)
  const { openModal, closeModal, isOpen } = useEarningsModal()
  const user = currentUser()

  const { partner } = usePartner()

  const [earningsSummaries, setEarningsSummaries] = useState<
    Array<EarningsSummaryDTO>
  >([])

  const [earningsTotal, setEarningsTotal] =
    useState<TEarningsSummary>(DEFAULT_EARNINGS)
  const createPath = useCreatePath()

  useFetchEarnings({
    startDate,
    endDate,
    cb: useCallback(
      (summaries, total) => {
        setEarningsSummaries(summaries)
        setEarningsTotal(total)
      },
      [setEarningsSummaries, setEarningsTotal]
    )
  })

  const earningsFilters = [
    <EarningsFilter
      label="Earnings Filters"
      key="earningsFilters"
      source="earningsFilter"
      setStartDate={setStartDate}
      setEndDate={setEndDate}
      minDate={partner?.created_at?.split('T')[0]}
      alwaysOn
    />
  ]

  const earningsSortDefaults: SortPayload = {
    field: 'total_amount_in_cents',
    order: 'DESC'
  }

  return (
    <>
      <List
        sort={earningsSortDefaults}
        sx={{ paddingBottom: '50px' }}
        filters={earningsFilters}
        empty={false}
      >
        <Modal open={isOpen} onClose={closeModal} maxWidth="90%">
          <Box
            sx={{
              maxWidth: 600,
              borderRadius: 1,
              p: 6
            }}
          >
            <Box
              sx={{
                fontSize: '2rem',
                fontWeight: 'bold',
                borderBottom: '1px solid white',
                pb: '1rem',
                mb: '1rem'
              }}
            >
              Processing
            </Box>
            <span>We are currently downloading your earnings report.</span>
            <span>
              You will receive an email at "{user?.email}" as soon as the report
              is available.
            </span>
          </Box>
        </Modal>
        <EarningsSummary summary={earningsTotal} />
        <EarningsChart
          summaries={earningsSummaries}
          startDate={startDate?.toISOString()?.slice(0, 10)}
          endDate={endDate?.toISOString()?.slice(0, 10)}
        />
        <Datagrid bulkActionButtons={false}>
          <TextField source="id" label="ID" />
          {isVendor() && <TextField source="vendor_sku" label="Vendor SKU" />}
          <TextField source="name" label="Name" />
          {!isVendor() && <DateField source="active_at" label="Start" />}
          {!isVendor() && <DateField source="expires_at" label="End" />}
          <NumberField
            source="items"
            sortBy="gross_line_item_count"
            label="Total Sold"
          />
          {isVendor() && (
            <FunctionField
              label={
                <EarningsCustomField
                  text="Refunds"
                  onClick={() => {
                    handleBreakdownCsv(
                      'refunds',
                      startDate?.toISOString() || '',
                      endDate?.toISOString() || ''
                    )
                  }}
                  iconSize="20px"
                />
              }
              render={({ refunds, id }) => (
                <EarningsCustomField
                  text={refunds}
                  sx={{ justifyContent: 'center' }}
                  onClick={() => {
                    handleBreakdownCsv(
                      'refunds',
                      startDate?.toISOString() || '',
                      endDate?.toISOString() || '',
                      id
                    )
                  }}
                />
              )}
            />
          )}
          {!isVendor() && (
            <NumberField
              source="refunds"
              sortBy="refunded_line_item_count"
              label="Refunds"
            />
          )}
          <NumberField
            source="net_items"
            sortBy="net_line_item_count"
            label="Net Sold"
          />
          {isVendor() && (
            <FunctionField
              label={
                <EarningsCustomField
                  text="Chargebacks"
                  onClick={() => {
                    handleBreakdownCsv(
                      'chargebacks',
                      startDate?.toISOString() || '',
                      endDate?.toISOString() || ''
                    )
                  }}
                  iconSize="20px"
                />
              }
              render={({ chargeback_items: chargebackItems, id }) => (
                <EarningsCustomField
                  text={chargebackItems}
                  sx={{ justifyContent: 'center' }}
                  onClick={() => {
                    handleBreakdownCsv(
                      'chargebacks',
                      startDate?.toISOString() || '',
                      endDate?.toISOString() || '',
                      id
                    )
                  }}
                />
              )}
            />
          )}
          {!isVendor() && (
            <NumberField
              source="earnings"
              label="Earnings"
              sortBy="total_amount_in_cents"
              options={{ style: 'currency', currency: 'USD' }}
            />
          )}
          {isVendor() && (
            <FunctionField
              label={
                <EarningsCustomField
                  text="Earnings"
                  onClick={() => {
                    sendEarningsToEmail({
                      startAt: startDate?.toISOString() || '',
                      endAt: endDate?.toISOString() || ''
                    })
                    openModal()
                  }}
                  iconSize="20px"
                />
              }
              render={({ earnings, id }) => (
                <EarningsCustomField
                  text={formatCurrency(earnings)}
                  onClick={() => {
                    sendEarningsToEmail({
                      saleId: id,
                      startAt: startDate?.toISOString() || '',
                      endAt: endDate?.toISOString() || ''
                    })
                    openModal()
                  }}
                />
              )}
            />
          )}
          {isVendor() && <TextField source="period_terms" label="Terms" />}
          <FunctionField
            label={
              <Tooltip title="Click graph icon for sale details.">
                <IconButton>
                  <Info width="20px" height="20px" />
                </IconButton>
              </Tooltip>
            }
            render={({ id }) => (
              <Link
                to={createPath({
                  resource: 'sales',
                  type: 'show',
                  id
                })}
              >
                <Graph height="25px" />
              </Link>
            )}
          />
        </Datagrid>
      </List>
    </>
  )
}

export default EarningsList
